import { useEffect, useState } from "react";
import { Input } from "@heroui/react";
import {
  EditableSelectComponent,
  EditableTextareaComponent,
  SectionHeader,
} from "components/index";
import {
  EmergencyLightingOnEveryFloorStatus,
  SmokeDetectorsOnEveryFloorStatus,
  FireExtinguisherOnEveryFloorStatus,
  FireExtinguisherInElevatorRoomStatus,
  FireHoseReelStatus,
  FireSecurityBuildingInfoDto,
  FireEvacuationStatus,
  FireProofDoorsStatus,
  FireStairStatus,
} from "shared/types/fire-security";
import { toast } from "react-toastify";
import { fetchWithAuth } from "shared/api/fetch-with-auth";
import { API_URL } from "constants/index";
import { useSession } from "@clerk/clerk-react";
import { AlertTriangle } from "assets/icons";
import SearchComponent, {
  SearchMode,
} from "shared/components/searchBar/components/searchComponent";
import { ResourceType } from "shared/types/resource";

const FireSecurityBuildingInfoComponent = ({
  fireSecurityBuildingInfo,
  handleFireSecurityInfoChangeInParent,
  handleSaveInParent,
  handleCloseParent,
  onEditClick,
  parentEditMode = true, // isLoading = false,
  // building,
}: any) => {
  const [editMode, setEditMode] = useState<boolean>(parentEditMode || true);
  const [editedFireSecurityInfo, setEditedFireSecurityInfo] =
    useState<FireSecurityBuildingInfoDto | null>(null);
  // const [fileModalOpen, setFileModalOpen] = useState<boolean>(false);
  // const [selectedFileLabel, setSelectedFileLabel] = useState<FileLabel | null>(
  //     null,
  // );
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  const { session } = useSession();

  const getStatusColorForResource = (resourceId: any) => {
    if (resourceId) return "bg-green-500";
    else return "bg-transparent";
  };

  const getStatusColor = (status: any) => {
    switch (status) {
      case EmergencyLightingOnEveryFloorStatus.OK:
      case SmokeDetectorsOnEveryFloorStatus.OK:
      case FireExtinguisherOnEveryFloorStatus.OK:
      case FireExtinguisherInElevatorRoomStatus.OK:
      case FireHoseReelStatus.NOT_REQUIRED:
      case EmergencyLightingOnEveryFloorStatus.NOT_REQUIRED:
      case FireExtinguisherInElevatorRoomStatus.NO_ELEVATOR:
      case FireHoseReelStatus.OK:
      case FireProofDoorsStatus.NOT_REQUIRED:
      case FireProofDoorsStatus.OK:
      case FireEvacuationStatus.OK:
      case FireEvacuationStatus.NOT_REQUIRED:
        return "bg-green-500";
      case FireExtinguisherOnEveryFloorStatus.PARTIAL:
      case SmokeDetectorsOnEveryFloorStatus.PARTIAL:
      case EmergencyLightingOnEveryFloorStatus.PARTIAL:
        return "bg-orange-500";
      case EmergencyLightingOnEveryFloorStatus.NOT_OK:
      case FireEvacuationStatus.NOT_OK:
      case SmokeDetectorsOnEveryFloorStatus.NOT_OK:
      case FireExtinguisherOnEveryFloorStatus.NOT_OK:
      case FireExtinguisherInElevatorRoomStatus.NOT_OK:
      case FireProofDoorsStatus.NOT_OK:
      case FireHoseReelStatus.NOT_OK:
        return "bg-red-500";
      default:
        return "bg-transparent";
    }
  };

  useEffect(() => {
    console.log(fireSecurityBuildingInfo);
    setEditedFireSecurityInfo(fireSecurityBuildingInfo);
  }, []); // don't use the dependency here because it will refresh the component on tab switch

  const localOnEditClick = () => {
    setEditMode((prevMode) => !prevMode);
    if (!editMode) {
      setEditedFireSecurityInfo(fireSecurityBuildingInfo);
    }
  };

  useEffect(() => {
    setEditMode(parentEditMode);
  }, [parentEditMode]);

  const updateFireSecurityInfo = (updatedFields: any) => {
    setHasUnsavedChanges(true);
    setEditedFireSecurityInfo((prev: any) => ({ ...prev, ...updatedFields }));

    if (handleFireSecurityInfoChangeInParent) {
      handleFireSecurityInfoChangeInParent({
        ...fireSecurityBuildingInfo,
        ...updatedFields,
      });
    }
  };

  const onUpdateFireSecurityRemarks = (remarks: string) => {
    updateFireSecurityInfo({ remarks: remarks });
  };

  const handleSave = async () => {
    setHasUnsavedChanges(false);

    if (handleSaveInParent) {
      handleSaveInParent();
    } else {
      try {
        await fetchWithAuth(
          `${API_URL}/fire-security/building-info`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(editedFireSecurityInfo),
          },
          session,
        );
        toast.success("Brandveiligheidsgegevens succesvol bijgewerkt!");
      } catch (e) {
        console.error(e);
        toast.error(
          "Er is een fout opgetreden bij het bijwerken van de brandveiligheidsgegevens.",
        );
      }
    }
  };

  // const handleClose = () => {
  //     setSelectedFileLabel(null);
  //     setFileModalOpen(false);
  // };

  const handleInspectorSelect = (resource: any) => {
    updateFireSecurityInfo({ inspectorResourceId: resource.id });
  };

  return (
    <section
      key="fireSecurity"
      className="flex flex-col gap-0 rounded-md bg-white"
    >
      <SectionHeader
        title="Brandveiligheid"
        icon={<AlertTriangle />}
        editMode={editMode}
        onEditClick={onEditClick || localOnEditClick}
        onSaveClick={() => handleSave()}
        sticky={false}
        showEditToggle={true}
        hasUnsavedChanges={hasUnsavedChanges}
        onCloseClick={() => {
          if (handleCloseParent) handleCloseParent();
        }}
        hideCloseAction={true}
      />
      <div className="gap-3 sm:grid sm:grid-cols-2">
        <div className="flex flex-col gap-3 rounded-md border border-neutral-primary/20 p-3">
          <header className="flex items-center gap-2 text-sm font-medium">
            <span
              className={`inline-block h-5 w-5 rounded-full border border-neutral-primary/30 ${getStatusColor(
                editedFireSecurityInfo?.emergencyLightingOnEveryFloorStatus,
              )}`}
            />
            <p>Noodverlichting op elke verdieping?</p>
          </header>
          <EditableSelectComponent
            label="Selecteer een optie"
            value={
              editedFireSecurityInfo?.emergencyLightingOnEveryFloorStatus || ""
            }
            options={Object.values(EmergencyLightingOnEveryFloorStatus).map(
              (value) => ({
                label: value,
                value,
              }),
            )}
            editMode={editMode}
            onChange={(newStatus) =>
              updateFireSecurityInfo({
                emergencyLightingOnEveryFloorStatus: newStatus,
              })
            }
          />
        </div>
        <div className="flex flex-col gap-3 rounded-md border border-neutral-primary/20 p-3">
          <header className="flex items-center gap-2 text-sm font-medium">
            <span
              className={`inline-block h-5 w-5 rounded-full border border-neutral-primary/30 ${getStatusColor(
                editedFireSecurityInfo?.smokeDetectorsOnEveryFloorStatus,
              )}`}
            />
            <p>Rookmelders op elke verdieping?</p>
          </header>
          <EditableSelectComponent
            label="Selecteer een optie"
            value={
              editedFireSecurityInfo?.smokeDetectorsOnEveryFloorStatus || ""
            }
            options={Object.values(SmokeDetectorsOnEveryFloorStatus).map(
              (value) => ({
                label: value,
                value,
              }),
            )}
            editMode={editMode}
            onChange={(newStatus) =>
              updateFireSecurityInfo({
                smokeDetectorsOnEveryFloorStatus: newStatus,
              })
            }
          />
        </div>
        <div className="flex flex-col gap-3 rounded-md border border-neutral-primary/20 p-3">
          <header className="flex items-center gap-2 text-sm font-medium">
            <span
              className={`inline-block h-5 w-5 rounded-full border border-neutral-primary/30 ${getStatusColor(
                editedFireSecurityInfo?.fireEvacuationStatus,
              )}`}
            />
            <p>
              Evacuatiepictogrammen op elke verdieping + evacuatieplan aanwezig?
            </p>
          </header>
          <EditableSelectComponent
            label="Selecteer een optie"
            value={editedFireSecurityInfo?.fireEvacuationStatus || ""}
            options={Object.values(FireEvacuationStatus).map((value) => ({
              label: value,
              value,
            }))}
            editMode={editMode}
            onChange={(newStatus) =>
              updateFireSecurityInfo({
                fireEvacuationStatus: newStatus,
              })
            }
          />
        </div>
        <div className="flex flex-col gap-3 rounded-md border border-neutral-primary/20 p-3">
          <header className="flex items-center gap-2 text-sm font-medium">
            <span
              className={`inline-block h-5 w-5 rounded-full border border-neutral-primary/30 ${getStatusColor(
                editedFireSecurityInfo?.fireExtinguisherOnEveryFloorStatus,
              )}`}
            />
            <p>Brandblussers op elke verdieping?</p>
          </header>
          <EditableSelectComponent
            label="Selecteer een optie"
            value={
              editedFireSecurityInfo?.fireExtinguisherOnEveryFloorStatus || ""
            }
            options={Object.values(FireExtinguisherOnEveryFloorStatus).map(
              (value) => ({
                label: value,
                value,
              }),
            )}
            editMode={editMode}
            onChange={(newStatus) =>
              updateFireSecurityInfo({
                fireExtinguisherOnEveryFloorStatus: newStatus,
              })
            }
          />
        </div>
        <div className="flex flex-col gap-3 rounded-md border border-neutral-primary/20 p-3">
          <header className="flex items-center gap-2 text-sm font-medium">
            <span
              className={`inline-block h-5 w-5 rounded-full border border-neutral-primary/30 ${getStatusColor(
                editedFireSecurityInfo?.fireStairStatus,
              )}`}
            />
            <p>Brandtrap aanwezig?</p>
          </header>
          <EditableSelectComponent
            label="Selecteer een optie"
            value={editedFireSecurityInfo?.fireStairStatus || ""}
            options={Object.values(FireStairStatus).map((value) => ({
              label: value,
              value,
            }))}
            editMode={editMode}
            onChange={(newStatus) =>
              updateFireSecurityInfo({
                fireStairStatus: newStatus,
              })
            }
          />
        </div>
        <div className="flex flex-col gap-3 rounded-md border border-neutral-primary/20 p-3">
          <header className="flex items-center gap-2 text-sm font-medium">
            <span
              className={`inline-block h-5 w-5 rounded-full border border-neutral-primary/30 ${getStatusColor(
                editedFireSecurityInfo?.fireExtinguisherInElevatorRoomStatus,
              )}`}
            />
            <p>Brandblusser in liftkamer?</p>
          </header>
          <EditableSelectComponent
            label="Selecteer een optie"
            value={
              editedFireSecurityInfo?.fireExtinguisherInElevatorRoomStatus || ""
            }
            options={Object.values(FireExtinguisherInElevatorRoomStatus).map(
              (value) => ({
                label: value,
                value,
              }),
            )}
            editMode={editMode}
            onChange={(newStatus) =>
              updateFireSecurityInfo({
                fireExtinguisherInElevatorRoomStatus: newStatus,
              })
            }
          />
        </div>
        <div className="flex flex-col gap-3 rounded-md border border-neutral-primary/20 p-3">
          <header className="flex items-center gap-2 text-sm font-medium">
            <span
              className={`inline-block h-5 w-5 rounded-full border border-neutral-primary/30 ${getStatusColor(
                editedFireSecurityInfo?.fireHoseReelStatus,
              )}`}
            />
            <p>Brandslanghaspel?</p>
          </header>
          <EditableSelectComponent
            label="Selecteer een optie"
            value={editedFireSecurityInfo?.fireHoseReelStatus || ""}
            options={Object.values(FireHoseReelStatus).map((value) => ({
              label: value,
              value,
            }))}
            editMode={editMode}
            onChange={(newStatus) =>
              updateFireSecurityInfo({ fireHoseReelStatus: newStatus })
            }
          />
        </div>
        <div className="flex flex-col gap-3 rounded-md border border-neutral-primary/20 p-3">
          <header className="flex items-center gap-2 text-sm font-medium">
            <span
              className={`inline-block h-5 w-5 rounded-full border border-neutral-primary/30 ${getStatusColor(
                editedFireSecurityInfo?.fireSecurityLastInspectionDate,
              )}`}
            />
            <p>Laatste inspectiedatum?</p>
          </header>
          <Input
            label="Laatste inspectiedatum"
            type="date"
            value={
              (editedFireSecurityInfo?.fireSecurityLastInspectionDate as any) ||
              ""
            }
            placeholder="Kies een datum"
            onChange={(e) =>
              updateFireSecurityInfo({
                fireSecurityLastInspectionDate: e.target.value,
              })
            }
            classNames={{
              input:
                "text-neutral-primary placeholder:text-neutral-secondary-mid pt-2.5",
              inputWrapper:
                "p-2 min-h-fit h-fit rounded-sm bg-white shadow-form-element data-[hover=true]:bg-white group-data-[focus=true]:bg-white data-[hover=true]:shadow-form-element-hv group-data-[focus=true]:shadow-form-element-fc",
            }}
          />
        </div>
        <div className="flex flex-col gap-3 rounded-md border border-neutral-primary/20 p-3">
          <header className="flex items-center gap-2 text-sm font-medium">
            <span
              className={`inline-block h-5 w-5 rounded-full border border-neutral-primary/30 ${getStatusColor(
                editedFireSecurityInfo?.fireProofDoors,
              )}`}
            />
            <p>Brandwerende deuren?</p>
          </header>
          <EditableSelectComponent
            label="Selecteer een optie"
            value={editedFireSecurityInfo?.fireProofDoors || ""}
            options={Object.values(FireProofDoorsStatus).map((value) => ({
              label: value,
              value,
            }))}
            editMode={editMode}
            onChange={(newStatus) =>
              updateFireSecurityInfo({
                fireProofDoors: newStatus,
              })
            }
          />
        </div>
        <div className="flex flex-col gap-3 rounded-md border border-neutral-primary/20 p-3">
          <header className="flex items-center gap-2 text-sm font-medium">
            <span
              className={`inline-block h-5 w-5 rounded-full border border-neutral-primary/30 ${getStatusColor(
                editedFireSecurityInfo?.fireSecurityNextInspectionDate,
              )}`}
            />
            <p>Volgende inspectiedatum?</p>
          </header>
          <Input
            label="Volgende inspectiedatum"
            type="date"
            value={
              (editedFireSecurityInfo?.fireSecurityNextInspectionDate as any) ||
              ""
            }
            placeholder="Kies een datum"
            onChange={(e) =>
              updateFireSecurityInfo({
                fireSecurityNextInspectionDate: e.target.value,
              })
            }
            classNames={{
              input:
                "text-neutral-primary placeholder:text-neutral-secondary-mid pt-2.5",
              inputWrapper:
                "p-2 min-h-fit h-fit rounded-sm bg-white shadow-form-element data-[hover=true]:bg-white group-data-[focus=true]:bg-white data-[hover=true]:shadow-form-element-hv group-data-[focus=true]:shadow-form-element-fc",
            }}
          />
        </div>
        <div className="flex flex-col gap-3 rounded-md border border-neutral-primary/20 p-3">
          <header className="flex items-center gap-2 text-sm font-medium">
            <span
              className={`inline-block h-5 w-5 rounded-full border border-neutral-primary/30 ${getStatusColorForResource(
                editedFireSecurityInfo?.inspectorResourceId,
              )}`}
            />
            <p>Partij die de laatste keuring heeft uitgevoerd?</p>
          </header>
          <SearchComponent
            label="Keuringsorganisme / Tegenpartij"
            onSelect={handleInspectorSelect}
            resourceFilter={[ResourceType.COMPANY, ResourceType.CONTACT]}
            initialValue={editedFireSecurityInfo?.inspectorResourceId}
            initialValueType="id"
            hasEditToggle={true}
            mode={SearchMode.SEARCH_AND_PRESENT}
            editMode={
              editedFireSecurityInfo?.inspectorResourceId ? false : true
            }
          />
        </div>
        <div className="flex flex-col gap-3 rounded-md border border-neutral-primary/20 p-3">
          <header className="flex items-center gap-2 text-sm font-medium">
            <span
              className={`inline-block h-5 w-5 rounded-full border border-neutral-primary/30 ${getStatusColor(
                editedFireSecurityInfo?.remarks,
              )}`}
            />
            <p>Bijkomende opmerkingen?</p>
          </header>
          <EditableTextareaComponent
            label="Geef opmerkingen of toelichting hier"
            value={editedFireSecurityInfo?.remarks || ""}
            isLoading={false}
            editMode={editMode}
            onChange={onUpdateFireSecurityRemarks}
          />
        </div>
      </div>
    </section>
  );
};

export default FireSecurityBuildingInfoComponent;
